// src/redux/contextRadar/contextRadarSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchContextRadarForTicker,
  fetchContextRadarForWatchlist,
  fetchGlobalContextRadar,
} from "./contextRadarActions";

const initialState = {
  // Per-ticker events
  byTicker: {}, // { AAPL: { data: [], loading: false, error: null, lastFetch: timestamp } }
  
  // Watchlist aggregated events
  watchlist: {
    data: [],
    loading: false,
    error: null,
    lastFetch: null,
  },
  
  // Global platform events
  global: {
    data: [],
    loading: false,
    error: null,
    lastFetch: null,
  },
  
  // UI state
  filters: {
    eventTypes: [], // [] = all, or ['sentiment', 'news', 'filing', 'abnormal']
    rarities: [],   // [] = all, or ['Common', 'Notable', 'Unusual', 'Rare']
    timeframe: '7d', // '24h', '7d', '1m', 'all'
  },
  
  // Selected event for detail view
  selectedEvent: null,
};

const contextRadarSlice = createSlice({
  name: "contextRadar",
  initialState,
  reducers: {
    // Clear data
    clearContextRadarForTicker(state, action) {
      const ticker = action.payload;
      delete state.byTicker[ticker];
    },
    
    clearWatchlistContextRadar(state) {
      state.watchlist = { data: [], loading: false, error: null, lastFetch: null };
    },
    
    clearGlobalContextRadar(state) {
      state.global = { data: [], loading: false, error: null, lastFetch: null };
    },
    
    // UI state management
    setEventTypeFilter(state, action) {
      state.filters.eventTypes = action.payload;
    },
    
    setRarityFilter(state, action) {
      state.filters.rarities = action.payload;
    },
    
    setTimeframeFilter(state, action) {
      state.filters.timeframe = action.payload;
    },
    
    resetFilters(state) {
      state.filters = initialState.filters;
    },
    
    selectEvent(state, action) {
      state.selectedEvent = action.payload;
    },
    
    clearSelectedEvent(state) {
      state.selectedEvent = null;
    },
  },

  extraReducers: (builder) => {
    // ====================================================================
    // TICKER CONTEXT RADAR
    // ====================================================================
    builder
      .addCase(fetchContextRadarForTicker.pending, (state, action) => {
        const { ticker } = action.meta.arg;
        
        if (!state.byTicker[ticker]) {
          state.byTicker[ticker] = { data: [], loading: true, error: null, lastFetch: null };
        } else {
          state.byTicker[ticker].loading = true;
          state.byTicker[ticker].error = null;
        }
      })
      
      .addCase(fetchContextRadarForTicker.fulfilled, (state, action) => {
        const { ticker, data } = action.payload;
        state.byTicker[ticker] = {
          data,
          loading: false,
          error: null,
          lastFetch: Date.now(),
        };
      })
      
      .addCase(fetchContextRadarForTicker.rejected, (state, action) => {
        const { ticker } = action.meta.arg;
        state.byTicker[ticker] = {
          data: [],
          loading: false,
          error: action.payload || "Failed to load events",
          lastFetch: null,
        };
      });

    // ====================================================================
    // WATCHLIST CONTEXT RADAR
    // ====================================================================
    builder
      .addCase(fetchContextRadarForWatchlist.pending, (state) => {
        state.watchlist.loading = true;
        state.watchlist.error = null;
      })
      
      .addCase(fetchContextRadarForWatchlist.fulfilled, (state, action) => {
        state.watchlist.data = action.payload.data || [];
        state.watchlist.loading = false;
        state.watchlist.error = null;
        state.watchlist.lastFetch = Date.now();
      })
      
      .addCase(fetchContextRadarForWatchlist.rejected, (state, action) => {
        state.watchlist.data = [];
        state.watchlist.loading = false;
        state.watchlist.error = action.payload || "Failed to load watchlist events";
        state.watchlist.lastFetch = null;
      });

    // ====================================================================
    // GLOBAL CONTEXT RADAR
    // ====================================================================
    builder
      .addCase(fetchGlobalContextRadar.pending, (state) => {
        state.global.loading = true;
        state.global.error = null;
      })
      
      .addCase(fetchGlobalContextRadar.fulfilled, (state, action) => {
        state.global.data = action.payload || [];
        state.global.loading = false;
        state.global.error = null;
        state.global.lastFetch = Date.now();
      })
      
      .addCase(fetchGlobalContextRadar.rejected, (state, action) => {
        state.global.data = [];
        state.global.loading = false;
        state.global.error = action.payload || "Failed to load global events";
        state.global.lastFetch = null;
      });
  },
});

// ====================================================================
// ACTIONS
// ====================================================================
export const {
  clearContextRadarForTicker,
  clearWatchlistContextRadar,
  clearGlobalContextRadar,
  setEventTypeFilter,
  setRarityFilter,
  setTimeframeFilter,
  resetFilters,
  selectEvent,
  clearSelectedEvent,
} = contextRadarSlice.actions;

// ====================================================================
// SELECTORS
// ====================================================================

// Basic selectors
export const selectContextRadarForTicker = (state, ticker) => {
  return state.contextRadar?.byTicker?.[ticker] || { data: [], loading: false, error: null };
};

export const selectWatchlistContextRadar = (state) => {
  return state.contextRadar?.watchlist || { data: [], loading: false, error: null };
};

export const selectGlobalContextRadar = (state) => {
  return state.contextRadar?.global || { data: [], loading: false, error: null };
};

// Filtered selectors
export const selectFilteredWatchlistEvents = (state) => {
  const { data } = state.contextRadar?.watchlist || { data: [] };
  const filters = state.contextRadar?.filters || {};
  
  return filterEvents(data, filters);
};

export const selectFilteredTickerEvents = (state, ticker) => {
  const { data } = state.contextRadar?.byTicker?.[ticker] || { data: [] };
  const filters = state.contextRadar?.filters || {};
  
  return filterEvents(data, filters);
};

// UI selectors
export const selectContextRadarFilters = (state) => {
  return state.contextRadar?.filters || initialState.filters;
};

export const selectSelectedEvent = (state) => {
  return state.contextRadar?.selectedEvent;
};

// Stats selectors
export const selectEventStats = (state) => {
  const { data } = state.contextRadar?.watchlist || { data: [] };
  
  if (!data || data.length === 0) {
    return { total: 0, byType: {}, byRarity: {}, hasRare: false };
  }
  
  const byType = data.reduce((acc, event) => {
    acc[event.event_type] = (acc[event.event_type] || 0) + 1;
    return acc;
  }, {});
  
  const byRarity = data.reduce((acc, event) => {
    if (event.rarity_label) {
      acc[event.rarity_label] = (acc[event.rarity_label] || 0) + 1;
    }
    return acc;
  }, {});
  
  const hasRare = data.some(e => e.rarity_label === 'Rare');
  
  return {
    total: data.length,
    byType,
    byRarity,
    hasRare,
  };
};

// Helper function to filter events
function filterEvents(events, filters) {
  if (!events || events.length === 0) return [];
  
  let filtered = [...events];
  
  // Filter by event type
  if (filters.eventTypes && filters.eventTypes.length > 0) {
    filtered = filtered.filter(event => 
      filters.eventTypes.includes(event.event_type)
    );
  }
  
  // Filter by rarity
  if (filters.rarities && filters.rarities.length > 0) {
    filtered = filtered.filter(event => 
      event.rarity_label && filters.rarities.includes(event.rarity_label)
    );
  }
  
  // Filter by timeframe (if needed in future)
  // Currently backend handles this via limit_per_ticker
  
  return filtered;
}

export default contextRadarSlice.reducer;